/**
 * Transformation Step — the "watching it become real" moment.
 *
 * Deliberately unhurried. Seven progress lines revealed at ~500ms
 * intervals with the actual transformation dispatched immediately.
 * The animation is decorative — the customer doesn't need to know
 * the backend hydration completed in <300ms.
 *
 * `prefers-reduced-motion` collapses everything to a single line
 * that reveals immediately.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/shared/ui/button";

interface Props {
  onReady: () => Promise<void> | void;
  onContinue: () => void;
}

const LINES = [
  "Reading your business notes…",
  "Understanding who you serve…",
  "Composing your Brand Brain…",
  "Preparing your Business Brain…",
  "Sketching your first month of campaigns…",
  "Filling your content calendar…",
  "Waking your marketing department…",
];

export function TransformationStep({ onReady, onContinue }: Props): JSX.Element {
  const [visible, setVisible] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const finished = useRef(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const reduced = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  useEffect(() => {
    let active = true;
    setError(null);
    void Promise.resolve().then(() => onReadyRef.current()).catch(() => {
      if (active) setError("We couldn't finish setting up your workspace. Your answers are safe.");
    });
    if (reduced) {
      setVisible(LINES.length);
      return;
    }
    const int = setInterval(() => {
      setVisible((v) => {
        if (v >= LINES.length) {
          if (!finished.current) finished.current = true;
          clearInterval(int);
          return v;
        }
        return v + 1;
      });
    }, 550);
    return () => { active = false; clearInterval(int); };
  }, [attempt, reduced]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-start justify-center gap-8" data-testid="transformation-step">
      <div className="space-y-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#c7a25a]">One moment</p>
        <h2 className="font-serif text-3xl leading-tight text-[#f5efe6] md:text-5xl" style={{ fontFamily: "'Fraunces', serif" }}>
          Building your <span className="italic text-[#c7a25a]">department</span>.
        </h2>
      </div>

      <ul className="space-y-3" data-testid="transformation-lines">
        {LINES.map((line, i) => {
          const shown = i < visible;
          const done = i < visible - 1;
          return (
            <li
              key={line}
              className={`flex items-center gap-3 transition-all duration-500 ${
                shown ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
              }`}
              aria-live="polite"
            >
              <span
                className={`grid h-6 w-6 place-items-center rounded-full border text-[11px] ${
                  done
                    ? "border-[#c7a25a] bg-[#c7a25a] text-[#0d1310]"
                    : shown
                    ? "border-[#c7a25a]/60 bg-[#c7a25a]/10 text-[#c7a25a]"
                    : "border-[#f5efe6]/10 text-[#f5efe6]/30"
                }`}
              >
                {done ? "✓" : ""}
              </span>
              <span className={`text-[15px] leading-relaxed ${done ? "text-[#f5efe6]/60" : "text-[#f5efe6]"}`}>
                {line}
              </span>
            </li>
          );
        })}
      </ul>

      {error && (
        <div className="w-full rounded-2xl border border-amber-300/30 bg-amber-300/10 p-5" role="alert" data-testid="transformation-error">
          <p className="text-sm text-[#f5efe6]">{error}</p>
          <Button
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
            className="mt-4 rounded-full bg-[#c7a25a] px-6 text-[#0d1310]"
            data-testid="transformation-retry"
          >
            Try again
          </Button>
          <button
            type="button"
            onClick={onContinue}
            className="ml-4 rounded-full border border-[#f5efe6]/20 px-6 py-3 text-sm text-[#f5efe6]"
            data-testid="transformation-continue"
          >
            Continue to onboarding
          </button>
        </div>
      )}
    </div>
  );
}
